import { createContext, useContext, useEffect, useState } from 'react';
import { getLiturgicalSeason, getSeasonCSSVars, updateFavicon } from '@/lib/liturgicalCalendar';

const LiturgicalThemeContext = createContext(null);

export function LiturgicalThemeProvider({ children }) {
  const [season, setSeason] = useState(() => getLiturgicalSeason());
  const [cssVars, setCssVars] = useState(() => getSeasonCSSVars(season));

  useEffect(() => {
    // Update season on mount and check daily
    const updateSeason = () => {
      const currentSeason = getLiturgicalSeason();
      setSeason(currentSeason);
      setCssVars(getSeasonCSSVars(currentSeason));
      
      // Apply CSS variables to document root
      const root = document.documentElement;
      const vars = getSeasonCSSVars(currentSeason);
      Object.entries(vars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
      
      // Update favicon to match liturgical season
      updateFavicon(currentSeason);
    };

    updateSeason();

    // Check for season change at midnight
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const msUntilMidnight = tomorrow - now;

    const midnightTimeout = setTimeout(() => {
      updateSeason();
      // Then check every 24 hours
      const dailyInterval = setInterval(updateSeason, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(midnightTimeout);
  }, []);

  return (
    <LiturgicalThemeContext.Provider value={{ season, cssVars }}>
      {children}
    </LiturgicalThemeContext.Provider>
  );
}

export function useLiturgicalTheme() {
  const context = useContext(LiturgicalThemeContext);
  if (!context) {
    throw new Error('useLiturgicalTheme must be used within a LiturgicalThemeProvider');
  }
  return context;
}

export default LiturgicalThemeContext;
